<template>
	<SideModal
		:show="show"
		@update:show="
			{
				if (!state.loading) $emit('update:show', $event);
			}
		"
		location="right"
		close-on-overlay-click
	>
		<div class="modal-container">
			<div class="modal-top">
				<div class="modal-title">
					<Typography color="primary" weight="600" size="1.25rem">
						{{ props.entityType ? props.entityType.charAt(0).toUpperCase() + props.entityType.slice(1) : '' }}
						Relationships:
					</Typography>
				</div>
				<v-btn
					@click="
						{
							if (!state.loading) $emit('update:show', false);
						}
					"
					variant="tonal"
					color="normal"
					icon
				>
					<v-icon color="gray600">mdi-close</v-icon>
				</v-btn>
			</div>
			<Typography color="gray900" weight="500" size="1.0rem" class="ms-2">
				{{ entityName }}
			</Typography>

			<div class="modal-body">
				<v-tabs
					v-model="state.tab"
					density="compact"
					class="custom-tabs"
					fixed-tabs
					color="secondary"
				>
					<v-tab v-for="tab in props.tabs" :key="tab" :value="tab">
						{{
							tab === "class"
								? "Classes"
								: tab.charAt(0).toUpperCase() + tab.slice(1) + "s"
						}}
					</v-tab>
				</v-tabs>

				<v-window v-model="state.tab">
					<RelationshipModalTab
						v-for="tab in tabs"
						:entity-type="props.entityType"
						:entity-id="props.entityId"
						:key="tab.relationType"
						:relation-type="tab.relationType"
						:search-label="tab.searchLabel"
						:relation-label="tab.relationLabel"
						:columns="tab.columns"
						:loading="loadingData"
						:state="state"
						:reload="reloadData"
						:data="tabData"
						:saving="state.saving"
						@entity-click="handleEntityClick"
					/>
				</v-window>
			</div>
		</div>
	</SideModal>
</template>

<script setup lang="ts">
import type { DirectoryType, NestedDirectoryType } from "@/api_types/school";
import useTeachers from "@/stores/school/teachers";
import useStudents from "@/stores/school/students";
import useParents from "@/stores/school/parents";
import useTeams from "@/stores/school/teams";
import useClasses from "@/stores/school/classes";
import useDirectoryNavigation from "@/stores/school/directoryNavigation";

import SideModal from "@/components/sideModal.vue";
import Typography from "@/components/typography.vue";
import RelationshipModalTab from "@/components/school/directory/RelationshipModalTab.vue";

import { asyncComputed } from "@/utils/asyncComputed";
import { computed, watch, reactive } from "vue";

const teacherStore = useTeachers();
const studentStore = useStudents();
const parentStore = useParents();
const teamStore = useTeams();
const classStore = useClasses();
const directoryNav = useDirectoryNavigation();

const props = defineProps<{
	directoryEntity?: DirectoryType;
	entityType?: "teacher" | "student" | "parent" | "team" | "class" | "guardian";
	entityName?: string;
	entityId?: string;
	tabs?: string[];
	show: boolean;
}>();

const state = reactive({
	loading: false,
	tab:
		(props.tabs && props.tabs[0]) ||
		("" as "student" | "class" | "parent" | "team" | "teacher" | "guardian"),
	saving: false,
	showForm: false,
	allSelected: false,
	classesData: [] as any[],
	studentData: [] as any[],
	parentsData: [] as any[],
	teamsData: [] as any[],
	teacherData: [] as any[],
});

const emit = defineEmits<{
  (e: 'update:show', ...args: any[]): void;
  (e: 'update:parent', ...args: any[]): void;
  (e: 'entity-click', entityData: any): void;
}>();

// Ensure classes data is loaded when the modal opens for the "add class" dropdown
watch(() => props.show, async (newVal) => {
	if (newVal && (!classStore.data || classStore.data.length === 0)) {
		await classStore.fetch();
	}
});

const { data: entityData, loading: loadingData, reload: reloadData } = asyncComputed(
	async () => {
		if (!props?.entityId) return null;
		switch (props.entityType) {
			case "teacher":
				return (await teacherStore.getTeacher(props.entityId)) as NestedDirectoryType;
			case "student":
				return (await studentStore.getStudent(props.entityId)) as NestedDirectoryType;
			case "parent":
				return (await parentStore.getParent(props.entityId)) as NestedDirectoryType;
			case "team":
				return (await teamStore.getTeam(props.entityId)) as NestedDirectoryType;
			case "class":
				return (await classStore.getClass(props.entityId)) as NestedDirectoryType;
			default:
				return null;
		}
	},
	[() => props.entityId ?? ""],
	undefined,
	true,
	{}
);

const tabData = computed(() => {
	let data: (any & { selected: boolean })[] = [];
	if (state.tab === "class") {
		data = (entityData.value as any)?.classes || [];
	} else if (state.tab === "student") {
		data = (entityData.value as any)?.students || [];
	} else if (state.tab === "parent") {
		data = (entityData.value as any)?.guardians || [];
	} else if (state.tab === "team") {
		data = (entityData.value as any)?.teams || [];
	} else if (state.tab === "teacher") {
		data = (entityData.value as any)?.teachers || [];
	} else if (state.tab === "guardian") {
		data = (entityData.value as any)?.guardians || [];
	}
	data.forEach((item) => {
		// If the all selected state is true, set all items to selected
		// If the all selected state is false, set all items to not selected
		item.selected = state.allSelected;
	});
	return data;
});

watch(
	() => state.tab,
	() => {
		state.showForm = false;
	}
);

const tabs = computed(() => [
	{
		relationType: "class",
		searchLabel: "Search Classes",
		relationLabel: "Class",
		availableItems: [],
		columns: [{ title: "Class Name", field: "title", sortKey: "title" }],
	},
	{
		relationType: "student",
		searchLabel: "Search Students",
		relationLabel: "Student",
		availableItems: [],
		columns: [
			{ title: "First Name", field: "child_fname", sortKey: "child_fname" },
			{ title: "Last Name", field: "child_lname", sortKey: "child_lname" },
		],
	},
	{
		relationType: "parent",
		searchLabel: "Search Parents",
		relationLabel: "Parent",
		availableItems: [],
		columns: [
			{ title: "First Name", field: "firstname", sortKey: "firstname" },
			{ title: "Last Name", field: "lastname", sortKey: "lastname" },
		],
	},
	{
		relationType: "team",
		searchLabel: "Search Teams",
		relationLabel: "Team",
		availableItems: [],
		columns: [
			{
				title: "Team Name",
				field: "team_name",
				sortKey: "team_name",
			},
		],
	},
	{
		relationType: "teacher",
		searchLabel: "Search Teachers",
		relationLabel: "Teacher",
		availableItems: [],
		columns: [
			{ title: "First Name", field: "teacher_fname", sortKey: "teacher_fname" },
			{ title: "Last Name", field: "teacher_lname", sortKey: "teacher_lname" },
		],
	},
]);

// Handle entity clicks from tabs
const handleEntityClick = (entityData: any) => {
	// Close the current modal
	emit('update:show', false);

	// Emit to parent component
	emit('entity-click', entityData);

	// Navigate to the entity using the store
	directoryNav.navigateToEntity(entityData);
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.modal-container {
	padding: 30px;

	.modal-top {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		margin-bottom: 20px;
	}

	.modal-title {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
	}

	.modal-body {
		display: flex;
		flex-direction: column;
		width: 100%;

		.custom-tabs {
			width: 100%;

			:deep(.v-tab) {
				min-width: 0;
				padding: 0 8px;
				font-size: 0.85rem;
				font-weight: 500;
				flex: 1;
				text-transform: none;
				letter-spacing: normal;
			}

			:deep(.v-slide-group__container) {
				overflow: visible;
			}
		}
	}
}
</style>
